import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES, TCOINS_COLORS, TCOINS_ICONS } from '../../context/TCoinsContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const TransactionHistory = ({ className = "" }) => {
  const { currentUser } = useAuth();
  const { TCOINS_TYPES, TCOINS_COLORS, TCOINS_ICONS } = useTCoins();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (currentUser) {
      loadTransactions();
    }
  }, [currentUser]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const userTransactions = userData.transactions || [];
        
        // Sort transactions
        const sortedTransactions = userTransactions.sort((a, b) => {
          if (sortBy === 'newest') {
            return new Date(b.timestamp) - new Date(a.timestamp);
          } else {
            return new Date(a.timestamp) - new Date(b.timestamp);
          }
        });
        
        setTransactions(sortedTransactions);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (filter === 'all') return true;
    if (filter === 'add') return transaction.type === 'add';
    if (filter === 'deduct') return transaction.type === 'deduct';
    if (filter === 'exchange') return transaction.type === 'exchange';
    return true;
  });

  const getTransactionIcon = (type, coinType) => {
    if (type === 'add') return '➕';
    if (type === 'deduct') return '➖';
    if (type === 'exchange') return '💱';
    return TCOINS_ICONS[coinType] || '🪙';
  };

  const getTransactionColor = (type) => {
    if (type === 'add') return 'text-green-600';
    if (type === 'deduct') return 'text-red-600';
    if (type === 'exchange') return 'text-blue-600';
    return 'text-gray-600';
  };

  const getTransactionTypeText = (type) => {
    const types = {
      add: 'ได้รับ',
      deduct: 'ใช้',
      exchange: 'แลกเปลี่ยน'
    };
    return types[type] || type;
  };

  const getCoinTypeName = (coinType) => {
    const names = {
      [TCOINS_TYPES.VIRTUAL]: 'Bonus Coins',
      [TCOINS_TYPES.REAL]: 'T Coins',
      [TCOINS_TYPES.DEMO]: 'ทองแดง',
      [TCOINS_TYPES.SILVER]: 'เงิน',
      [TCOINS_TYPES.GOLD]: 'ทอง',
      [TCOINS_TYPES.EMERALD]: 'มรกต',
      [TCOINS_TYPES.ONYX]: 'นิล',
      [TCOINS_TYPES.PURPLE]: 'ม่วง'
    };
    return names[coinType] || coinType;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalByType = (type) => {
    return transactions
      .filter(t => t.type === type)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2">กำลังโหลด...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md ${className}`}>
      {/* Header */}
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold text-gray-800">📊 ประวัติการใช้งาน T Coins</h3>
        <p className="text-sm text-gray-600">ติดตามการใช้งาน T Coins ของคุณ</p>
      </div>

      {/* Summary */}
      <div className="p-4 bg-gray-50">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600">
              {getTotalByType('add').toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">ได้รับทั้งหมด</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">
              {getTotalByType('deduct').toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">ใช้ทั้งหมด</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {transactions.length}
            </div>
            <div className="text-sm text-gray-600">รายการทั้งหมด</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b">
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm ${
              filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            ทั้งหมด
          </button>
          <button
            onClick={() => setFilter('add')}
            className={`px-3 py-1 rounded-full text-sm ${
              filter === 'add' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            ได้รับ
          </button>
          <button
            onClick={() => setFilter('deduct')}
            className={`px-3 py-1 rounded-full text-sm ${
              filter === 'deduct' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            ใช้
          </button>
          <button
            onClick={() => setFilter('exchange')}
            className={`px-3 py-1 rounded-full text-sm ${
              filter === 'exchange' ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            แลกเปลี่ยน
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">เรียงตาม:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="newest">ใหม่ล่าสุด</option>
            <option value="oldest">เก่าที่สุด</option>
          </select>
        </div>
      </div>

      {/* Transactions List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredTransactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📝</div>
            <div>ไม่มีประวัติการใช้งาน</div>
          </div>
        ) : (
          <div className="divide-y">
            {filteredTransactions.map((transaction, index) => (
              <div key={transaction.id || index} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`text-2xl ${getTransactionColor(transaction.type)}`}>
                      {getTransactionIcon(transaction.type, transaction.coinType)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">
                        {getTransactionTypeText(transaction.type)} {getCoinTypeName(transaction.coinType)} {transaction.amount.toLocaleString()} เหรียญ
                      </div>
                      <div className="text-sm text-gray-600">
                        {transaction.reason || 'ไม่ระบุเหตุผล'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(transaction.timestamp)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`font-semibold ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'add' ? '+' : '-'}{transaction.amount.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      ยอดคงเหลือ: {transaction.balance?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t bg-gray-50 text-center">
        <div className="text-sm text-gray-600">
          💡 ประวัติการใช้งานจะถูกบันทึกอัตโนมัติ
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;






